import React, {
  type InputHTMLAttributes,
  forwardRef,
  useState,
  useEffect,
} from 'react';

type SwitchProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'size' | 'type'
> & {
  size?: 'sm' | 'md' | 'lg';
  label?: string;
  labelPosition?: 'left' | 'right';
  variant?: 'default';
};

const Switch = forwardRef<HTMLInputElement, SwitchProps>((props, ref) => {
  const {
    size = 'md',
    label,
    labelPosition = 'right',
    className = '',
    disabled = false,
    checked: controlledChecked = false,
    onChange,
    ...rest
  } = props;

  const [internalChecked, setInternalChecked] = useState(controlledChecked);
  const isControlled = 'checked' in props;
  const checked = isControlled ? controlledChecked : internalChecked;

  useEffect(() => {
    if (isControlled) {
      setInternalChecked(controlledChecked);
    }
  }, [controlledChecked, isControlled]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isControlled) {
      setInternalChecked(e.target.checked);
    }
    onChange?.(e);
  };

  const sizeClasses = {
    sm: 'w-9 h-5',
    md: 'w-11 h-6',
    lg: 'w-13 h-7',
  };

  const thumbSizeClasses = {
    sm: 'w-3.5 h-3.5',
    md: 'w-4.5 h-4.5',
    lg: 'w-5.5 h-5.5',
  };

  const baseClasses =
    'relative inline-flex items-center rounded-full transition-all duration-200 cursor-pointer focus:outline-none';

  const stateClass = `bg-gray-200 hover:bg-gray-300 focus:ring-2 focus:ring-primary-200 ${
    checked ? '!bg-primary-500 hover:!bg-primary-700' : ''
  }`;

  const disabledClass = 'bg-gray-100 !cursor-not-allowed';

  const labelClasses = {
    default: 'text-gray-900 cursor-pointer',
    disabled: 'text-gray-400 cursor-not-allowed',
  };

  const computedInputClasses = `${baseClasses} ${sizeClasses[size]} ${className}`;
  const computedLabelClasses = `${labelClasses[disabled ? 'disabled' : 'default']} select-none`;

  const switchElement = (
    <div className='relative inline-flex items-center'>
      <input
        ref={ref}
        type='checkbox'
        className='sr-only'
        disabled={disabled}
        checked={checked}
        onChange={handleChange}
        {...rest}
      />
      <div
        role='switch'
        tabIndex={0}
        aria-checked={checked}
        className={`
          ${computedInputClasses}
          ${disabled ? disabledClass : stateClass}
        `}
      >
        <div
          className={`
            ${thumbSizeClasses[size]}
            bg-white rounded-full shadow-sm transition-transform duration-200
            ${checked ? 'translate-x-[calc(100%+2px)]' : 'translate-x-0'}
            ${size === 'sm' ? 'ml-0.75' : size === 'md' ? 'ml-0.75' : 'ml-0.75'}
            ${disabled ? 'cursor-not-allowed' : ''}
          `}
        />
      </div>
    </div>
  );

  if (!label) {
    return <div className='group'>{switchElement}</div>;
  }

  return (
    <label
      className={`group flex items-center gap-2 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
    >
      {labelPosition === 'left' && (
        <span className={computedLabelClasses}>{label}</span>
      )}
      {switchElement}
      {labelPosition === 'right' && (
        <span className={computedLabelClasses}>{label}</span>
      )}
    </label>
  );
});

Switch.displayName = 'Switch';

export default Switch;
